import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../utils/apiClient';

const BASE = process.env.REACT_APP_API_BASE_URL;

const PLAN_BADGE = {
    free:      'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400',
    essentiel: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300',
    premium:   'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300',
};

function StatBadge({ label, value, color }) {
    return (
        <div className="text-center">
            <p className={`text-2xl font-bold ${value > 0 ? color : 'text-gray-300 dark:text-gray-600'}`}>{value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{label}</p>
        </div>
    );
}

function formatDate(dt) {
    if (!dt) return '';
    const d = new Date(dt.replace(' ', 'T'));
    return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function ParentDashboard() {
    const navigate = useNavigate();
    const [students, setStudents]   = useState([]);
    const [loading, setLoading]     = useState(true);
    const [showLink, setShowLink]   = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [code, setCode]           = useState('');
    const [linkErr, setLinkErr]     = useState('');
    const [linkOk, setLinkOk]       = useState('');
    const [linking, setLinking]     = useState(false);
    const [unlinking, setUnlinking] = useState(null);
    const [createForm, setCreateForm] = useState({ name: '', email: '', password: '' });
    const [createErr, setCreateErr] = useState('');
    const [creating, setCreating]   = useState(false);
    const [createdInfo, setCreatedInfo] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res  = await apiClient(`${BASE}/parent_dashboard.php?action=my_students`);
            const json = await res.json();
            if (json.success) setStudents(json.students);
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleLink = async (e) => {
        e.preventDefault();
        setLinkErr('');
        setLinkOk('');
        const trimmed = code.trim().toUpperCase();
        if (!trimmed) return;
        setLinking(true);
        try {
            const res  = await apiClient(`${BASE}/parent_dashboard.php?action=link_student`, {
                method: 'POST',
                body: JSON.stringify({ code: trimmed }),
            });
            const json = await res.json();
            if (json.success) {
                setLinkOk(`${json.name} a bien été ajouté à votre liste.`);
                setCode('');
                setShowLink(false);
                await load();
            } else {
                setLinkErr(json.message || 'Code invalide.');
            }
        } finally { setLinking(false); }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setCreateErr('');
        if (createForm.password.length < 8) { setCreateErr('Mot de passe minimum 8 caractères.'); return; }
        setCreating(true);
        try {
            const res  = await apiClient(`${BASE}/parent_dashboard.php?action=create_and_link`, {
                method: 'POST',
                body: JSON.stringify(createForm),
            });
            const json = await res.json();
            if (json.success) {
                setCreatedInfo(json);
                setCreateForm({ name: '', email: '', password: '' });
                setShowCreate(false);
                await load();
            } else {
                setCreateErr(json.message || 'Erreur lors de la création.');
            }
        } finally { setCreating(false); }
    };

    const handleUnlink = async (student) => {
        if (!window.confirm(`Retirer ${student.name} de votre liste ?`)) return;
        setUnlinking(student.id);
        try {
            await apiClient(`${BASE}/parent_dashboard.php?action=unlink_student`, {
                method: 'POST',
                body: JSON.stringify({ student_id: student.id }),
            });
            setStudents(prev => prev.filter(s => s.id !== student.id));
        } finally { setUnlinking(null); }
    };

    if (loading) return <div className="text-center py-20 text-gray-400">Chargement…</div>;

    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Mes enfants</h2>
                    {students.length > 0 && (
                        <p className="text-sm text-gray-400 mt-0.5">{students.length} élève{students.length > 1 ? 's' : ''} suivi{students.length > 1 ? 's' : ''}</p>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => { setShowCreate(v => !v); setShowLink(false); setCreateErr(''); }}
                        className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 text-white text-sm font-medium transition-colors">
                        + Créer un compte élève
                    </button>
                    <button onClick={() => { setShowLink(v => !v); setShowCreate(false); setLinkErr(''); setCode(''); }}
                        className="px-3 py-1.5 rounded-lg border border-orange-300 dark:border-orange-500/40 text-orange-600 dark:text-orange-400 text-sm font-medium hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors">
                        Lier via code
                    </button>
                </div>
            </div>

            {/* Infos du compte créé */}
            {createdInfo && (
                <div className="mb-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-xl px-5 py-4">
                    <p className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2">
                        Compte créé pour {createdInfo.name}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">
                        <span className="font-medium">Identifiant de connexion :</span> {createdInfo.login_email}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">
                        <span className="font-medium">Mot de passe :</span> {createdInfo.password}
                    </p>
                    <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">Notez ces informations — le mot de passe ne sera plus affiché.</p>
                    <button onClick={() => setCreatedInfo(null)} className="mt-2 text-xs text-blue-400 hover:text-blue-600 underline">Fermer</button>
                </div>
            )}

            {/* Message de succès liaison */}
            {linkOk && (
                <div className="mb-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-500/30 rounded-xl px-4 py-3 flex items-center justify-between">
                    <p className="text-sm text-green-700 dark:text-green-300">{linkOk}</p>
                    <button onClick={() => setLinkOk('')} className="text-green-400 hover:text-green-600 ml-3 text-lg leading-none">×</button>
                </div>
            )}

            {/* Formulaire de création de compte */}
            {showCreate && (
                <form onSubmit={handleCreate}
                    className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl px-5 py-5 flex flex-col gap-4">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Créer un compte pour votre enfant</h3>
                        <p className="text-xs text-gray-400">L'email est optionnel — si vous n'en avez pas, un identifiant interne sera généré automatiquement.</p>
                    </div>
                    {createErr && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">{createErr}</p>}
                    <div className="flex flex-wrap gap-3">
                        <input type="text" placeholder="Prénom et nom *" value={createForm.name}
                            onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} required
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400" />
                        <input type="email" placeholder="Email (optionnel)" value={createForm.email}
                            onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400" />
                        <input type="password" placeholder="Mot de passe (8 min.) *" value={createForm.password}
                            onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} required
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400" />
                    </div>
                    <div className="flex gap-2 justify-end">
                        <button type="button" onClick={() => { setShowCreate(false); setCreateErr(''); }}
                            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                            Annuler
                        </button>
                        <button type="submit" disabled={creating}
                            className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                            {creating ? 'Création…' : 'Créer et lier'}
                        </button>
                    </div>
                </form>
            )}

            {/* Formulaire de liaison via code */}
            {showLink && (
                <form onSubmit={handleLink}
                    className="mb-6 bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/10 rounded-2xl px-5 py-5 flex flex-col sm:flex-row items-start sm:items-end gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
                            Code de l'élève <span className="text-gray-300">(visible dans ses paramètres)</span>
                        </label>
                        <input
                            type="text"
                            placeholder="ex. A3X7K2PQ"
                            value={code}
                            onChange={e => { setCode(e.target.value.toUpperCase()); setLinkErr(''); }}
                            maxLength={8}
                            autoFocus
                            className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-800 dark:text-white text-sm font-mono tracking-widest placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400 w-44" />
                        {linkErr && <p className="text-xs text-red-500">{linkErr}</p>}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={() => { setShowLink(false); setCode(''); setLinkErr(''); }}
                            className="px-3 py-2 rounded-lg border border-gray-200 dark:border-white/10 text-gray-500 text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                            Annuler
                        </button>
                        <button type="submit" disabled={linking}
                            className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white text-sm font-medium transition-colors">
                            {linking ? 'Recherche…' : 'Lier'}
                        </button>
                    </div>
                </form>
            )}

            {/* État vide */}
            {students.length === 0 && !showLink && (
                <div className="text-center py-20">
                    <p className="text-4xl mb-3">👨‍👩‍👧</p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">Aucun enfant lié pour l'instant.</p>
                    <p className="text-gray-400 text-xs mt-1">Cliquez sur « Lier un enfant » et entrez le code visible dans les paramètres de l'élève.</p>
                </div>
            )}

            {/* Cartes enfants */}
            <div className="flex flex-col gap-6">
                {students.map(s => (
                    <div key={s.id} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-white/10 overflow-hidden shadow-sm">
                        {/* En-tête carte */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-white/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-300 font-bold text-lg">
                                    {s.name.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <p className="font-semibold text-gray-900 dark:text-white">{s.name}</p>
                                        {s.plan_slug && (
                                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${PLAN_BADGE[s.plan_slug] || PLAN_BADGE.free}`}>
                                                {s.plan_name}
                                            </span>
                                        )}
                                    </div>
                                    {s.tuteurs?.length > 0 && (
                                        <p className="text-xs text-gray-400">
                                            {s.tuteurs.map(t => `${t.tuteur_name} (${t.subject_name})`).join(' · ')}
                                        </p>
                                    )}
                                    {s.token_limit > 0 && (() => {
                                        const pct = Math.min(100, Math.round((s.token_used / s.token_limit) * 100));
                                        const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-400' : 'bg-cyan-500';
                                        return (
                                            <div className="flex items-center gap-2 mt-1">
                                                <div className="w-20 bg-gray-100 dark:bg-white/10 rounded-full h-1">
                                                    <div className={`${color} h-1 rounded-full`} style={{ width: `${pct}%` }} />
                                                </div>
                                                <span className="text-[10px] text-gray-400">IA {pct}%</span>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                                {s.tuteurs?.map(t => (
                                    <button
                                        key={t.tuteur_id}
                                        onClick={() => navigate(`/messagerie?to=${t.tuteur_id}`)}
                                        className="text-xs px-2.5 py-1 rounded-lg bg-cyan-50 dark:bg-cyan-900/20 text-cyan-600 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-500/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/40 transition-colors font-medium">
                                        {t.subject_name}
                                    </button>
                                ))}
                                <button
                                    onClick={() => handleUnlink(s)}
                                    disabled={unlinking === s.id}
                                    className="text-xs text-gray-300 dark:text-gray-600 hover:text-red-400 dark:hover:text-red-400 transition-colors disabled:opacity-40 px-2 py-1">
                                    {unlinking === s.id ? '…' : 'Retirer'}
                                </button>
                            </div>
                        </div>

                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Colonne 1 : Stats */}
                            <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Activité</p>
                                <div className="flex gap-4">
                                    <StatBadge label="Exercices" value={parseInt(s.skills_done)}      color="text-indigo-600 dark:text-indigo-400" />
                                    <StatBadge label="Flashcards"   value={parseInt(s.flashcards_count)} color="text-cyan-600 dark:text-cyan-400" />
                                    <StatBadge label="Quizzes"      value={parseInt(s.quizzes_count)}    color="text-purple-600 dark:text-purple-400" />
                                </div>
                                {s.conversations_count > 0 && (
                                    <p className="text-xs text-gray-400 mt-3">
                                        {s.conversations_count} session{s.conversations_count > 1 ? 's' : ''} de coaching IA
                                    </p>
                                )}
                            </div>

                            {/* Colonne 2 : Classes */}
                            <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Classes</p>
                                {s.classes.length === 0 ? (
                                    <p className="text-xs text-gray-300 dark:text-gray-600">Aucune classe assignée</p>
                                ) : (
                                    <div className="flex flex-wrap gap-2">
                                        {s.classes.map((c, i) => (
                                            <div key={i} className="flex flex-col">
                                                <span className="text-xs px-2 py-1 rounded-full bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 font-medium">
                                                    {c.nom}
                                                </span>
                                                <span className="text-[10px] text-gray-400 mt-0.5 text-center">{c.professeur_name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Colonne 3 : Prochaines séances */}
                            <div>
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Prochaines séances</p>
                                {s.upcoming_events.length === 0 ? (
                                    <p className="text-xs text-gray-300 dark:text-gray-600">Aucune séance planifiée</p>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {s.upcoming_events.map((evt, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 shrink-0" />
                                                <div>
                                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{evt.title}</p>
                                                    <p className="text-[10px] text-gray-400">{formatDate(evt.start_time)}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
